import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { User, Calendar, Bell, Users, Shield } from 'lucide-react';
import ParentChildConnector from '@/components/dashboard/ParentChildConnector';
import ChildPrivacySettings from '@/components/dashboard/ChildPrivacySettings';
import OnlineStatusIndicator from '@/components/common/OnlineStatusIndicator';
import { useUnifiedUser } from '@/hooks/useUnifiedUser';
import { useChildConnections } from '@/hooks/useChildConnections';
import { useChildOnlineStatus } from '@/hooks/useChildOnlineStatus';
import { childPrivacyService } from '@/services/childPrivacyService';

const ParentDashboard: React.FC = () => {
  const { user } = useUnifiedUser();
  
  console.log('🎯 ParentDashboard - User data:', {
    id: user?.id,
    role: user?.role,
    username: user?.username,
    userType: user?.userType
  });
  
  const { 
    connectedChildren, 
    childTeams, 
    loading, 
    error, 
    hasConnections, 
    totalChildren, 
    totalChildTeams,
    refreshConnections 
  } = useChildConnections(user?.id || '', user?.role || '');

  // Get child IDs for online status tracking
  const childIds = useMemo(() => 
    connectedChildren.map(c => c.child_id), 
    [connectedChildren]
  );
  
  const { statuses: onlineStatuses, refreshStatuses } = useChildOnlineStatus(childIds);

  console.log('🎯 ParentDashboard - Connections data:', {
    connectedChildren,
    childTeams,
    loading,
    error,
    hasConnections,
    totalChildren,
    totalChildTeams,
    onlineStatuses
  });

  // Force refresh connections every time component mounts to ensure latest data
  React.useEffect(() => {
    if (user?.id && user?.role === 'parent' && !loading) {
      console.log('🔄 ParentDashboard - Force refreshing on mount');
      refreshConnections();
    }
  }, [user?.id, user?.role]);

  return (
    <div className="space-y-6">
      {/* Parent-Child Connection Section */}
      {user && (
        <ParentChildConnector 
          parentId={user.id} 
          userRole={user.role || 'parent'} 
        />
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-4 flex items-center space-x-4">
            <User className="h-8 w-8 text-sports-green" />
            <div>
              <h4 className="font-semibold text-white">Child Accounts</h4>
              <p className="text-sm text-gray-300">
                {loading ? 'Loading...' : `${totalChildren} connected`}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-4 flex items-center space-x-4">
            <Calendar className="h-8 w-8 text-sports-green" />
            <div>
              <h4 className="font-semibold text-white">Child Teams</h4>
              <p className="text-sm text-gray-300">
                {loading ? 'Loading...' : `${totalChildTeams} teams`}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-4 flex items-center space-x-4">
            <Bell className="h-8 w-8 text-sports-green" />
            <div>
              <h4 className="font-semibold text-white">Connection Status</h4>
              <p className="text-sm text-gray-300">
                {loading ? 'Loading...' : (hasConnections ? 'Active' : 'Ready to connect')}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Connected Children Activity */}
      <Card className="bg-gray-800 border-gray-700">
        <CardContent className="p-4">
          <h4 className="font-semibold mb-2 text-white">Connected Children</h4>
          {loading ? (
            <div className="p-3 bg-gray-700 rounded-md border border-gray-600">
              <p className="text-sm text-gray-300">Loading children data...</p>
            </div>
          ) : error ? (
            <div className="p-3 bg-red-900/20 rounded-md border border-red-700">
              <p className="text-sm text-red-300">Error loading children: {error}</p>
            </div>
          ) : hasConnections ? (
            <div className="space-y-2">
              {connectedChildren.map((connection) => {
                const childStatus = onlineStatuses[connection.child_id];
                return (
                  <div key={connection.id} className="p-3 bg-gray-700 rounded-md border border-gray-600">
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-sports-green" />
                        <span className="font-medium text-white">{connection.child?.username}</span>
                        {/* Online Status Indicator */}
                        {childStatus?.showOnlineStatus && (
                          <OnlineStatusIndicator
                            isOnline={childStatus.isOnline}
                            lastSeen={childStatus.lastSeen}
                            size="sm"
                          />
                        )}
                      </div>
                      <span className="text-xs bg-sports-green/20 text-sports-green px-2 py-1 rounded">
                        {connection.child?.age_group}
                      </span>
                    </div>
                    <p className="text-sm text-gray-300 mt-1">
                      Connected on {new Date(connection.connected_at).toLocaleDateString()}
                    </p>
                    {/* Show child's teams if any */}
                    {childTeams.filter(team => team.child_id === connection.child_id).length > 0 && (
                      <div className="mt-2 pt-2 border-t border-gray-600">
                        <p className="text-xs text-gray-400">
                          Teams: {childTeams.filter(team => team.child_id === connection.child_id)
                            .map(team => team.team_name).join(', ')}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-3 bg-gray-700 rounded-md border border-gray-600">
              <p className="text-sm text-gray-300">No children connected yet. Use the connection form above to link your child's account.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Child Privacy Settings Section - Only show for connected under-13 children */}
      {hasConnections && connectedChildren.some(c => 
        childPrivacyService.isUserUnder13(c.child?.age_group)
      ) && (
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Shield className="h-5 w-5 text-sports-green" />
              Manage Child Privacy Settings
            </CardTitle>
            <p className="text-gray-400 text-sm mt-1">
              Control what information is visible on your child's profile
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {connectedChildren
              .filter(c => childPrivacyService.isUserUnder13(c.child?.age_group))
              .map((connection) => (
                <ChildPrivacySettings
                  key={connection.id}
                  childId={connection.child_id}
                  childUsername={connection.child?.username || 'Child'}
                  parentId={user?.id || ''}
                  onSaved={() => {
                    refreshConnections();
                    refreshStatuses();
                  }}
                />
              ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ParentDashboard;
